import { AfterContentInit, Component, ContentChildren, ElementRef, forwardRef, Inject, Input, OnInit, QueryList } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { Permissions } from '../../common/constants';
import { PropertyInfo } from '../../common/properties';
import { CustomMatPaginatorIntl } from '../../dashboard-area/shared/custom-mat-paginator-intl.service';
import { Partner, PartnerType } from '../../model';
import { AbstractExportContextService } from '../../service/abstract-export-context.service';
import { AddButtonContextService, AddButtonResource } from '../../service/add-button-context.service';
import { AppService } from '../../service/app.service';
import { AuthenticationService } from '../../service/authentication.service';
import { NavigationService } from '../../service/navigation.service';
import { AbstractContextService } from '../../shared/class/abstract-context-service.class';
import { BulkDeleteElementType } from '../../shared/component/bulk-delete-dialog/bulk-delete-dialog.component';
import { ListSnackbarButtonType } from '../../shared/component/list-snackbar/list-snackbar.component';
import { BulkControl, ColumnComponentDisplayMode, ListWidgetV2Component } from '../../shared/list-widget-v2/list-widget-v2.components';
import { SearchFieldComponent, SearchTargetType } from '../../shared/search-field/search-field.component';
import { COMPONENT_DEFINITION_REF } from "../../shared/utility/component-definition-token";
import { ErrorUtility } from '../../utility/error-utility';
import { SnackbarService } from '../shared/snackbar.service';
import { WidgetPrintResizeCoordinatorService } from '../shared/widget-print-resize-coordinator.service';
import { PartnerListWidgetV2Service } from './partner-list-widget-v2.service';

@Component({
    selector: 'partner-list-widget-v2',
    template: require('./partner-list-widget-v2.component.html'),
    styles: [require('../../shared/list-widget-v2/list-widget-v2.css')],
    providers: [PartnerListWidgetV2Service, { provide: MatPaginatorIntl, useClass: CustomMatPaginatorIntl }]
})
export class PartnerListWidgetV2Component extends ListWidgetV2Component<Partner> implements OnInit, AfterContentInit {

    @Input() searchFields: string[] = ["name", "code"];

    @Input() enableActions = true;

    @Input() detailsPath: string;

    @Input() type: PartnerType = PartnerType.CHANNEL;

    @ContentChildren(COMPONENT_DEFINITION_REF) private columnComponents: QueryList<any>;

    private defaultProperties: { [name: string]: PropertyInfo } = {
        name: { label: 'partnerNameProperty', path: 'name', defaultFilter: null, defaultSorting: null },
        code: { label: 'codeProperty', path: 'code', defaultFilter: null, defaultSorting: null }
    };
    private includeParentsOnly;

    showIncludeAllPartners: boolean;
    includeAllPartners: boolean;
    bulkControls: BulkControl[] = [];

    constructor(
        @Inject(forwardRef(() => PartnerListWidgetV2Service)) private partnerListV2Service: PartnerListWidgetV2Service,
        @Inject(forwardRef(() => NavigationService)) private navigationService: NavigationService,
        @Inject(forwardRef(() => AuthenticationService)) authenticationService: AuthenticationService,
        @Inject(forwardRef(() => AppService)) appService: AppService,
        @Inject(forwardRef(() => AbstractExportContextService)) exportService: AbstractExportContextService,
        @Inject(forwardRef(() => AbstractContextService)) private contextService: AbstractContextService,
        @Inject(forwardRef(() => AddButtonContextService)) addButtonService: AddButtonContextService,
        @Inject(forwardRef(() => MatDialog)) dialog: MatDialog,
        @Inject(forwardRef(() => SnackbarService)) snackbarService: SnackbarService,
        @Inject(forwardRef(() => ElementRef)) elementRef: ElementRef,
        @Inject(forwardRef(() => WidgetPrintResizeCoordinatorService)) coordinator: WidgetPrintResizeCoordinatorService
    ) {
        super(appService, authenticationService, exportService, addButtonService, dialog, snackbarService, null, elementRef, coordinator);
    }

    ngOnInit() {
        this.searchFieldId = 'partner-list-query';
        this.popupSearchTarget = SearchTargetType.PARTNERS;
        this.checkIsMobile();
        this.writePermission = this.authenticationService.hasPermission(Permissions.WRITE_PARTNER);
        const currentPartner = this.contextService.getCurrentPartner();
        this.showAddButton = this.writePermission && this.enableActions && ((this.authenticationService.isOrganizationUser() && !currentPartner) || (this.authenticationService.isPartnerUser() && !this.authenticationService.getUser().partner.parentPartnerId));
        if (this.exportEnabled) {
            this.subscribeToExportServices();
        }
        this.includeParentsOnly = this.authenticationService.isOrganizationUser() && !this.contextService.getCurrentPartner();
        if (this.showAddButton) {
            this.subscribeToAddButtonVisibility(AddButtonResource.PARTNER);
        }
        this.showIncludeAllPartners = this.authenticationService.isOrganizationUser() && !this.contextService.getCurrentPartner() && this.type != PartnerType.DATA_RECIPIENT;
        this.preserveSelectedBetweenPages = true;
        this.bulkControlsEnabled = this.bulkControlsEnabled && this.authenticationService.hasPermission(Permissions.DELETE_PARTNER);
    }

    ngAfterContentInit(): void {
        this.initVisibleColumns();
        setTimeout(() => {      //waits for search field init
            this.getSearchFieldInputs();
            this.initCompleted = true;
            if (!this.isOnline()) {
                this.executeAdvancedSearch(null);
            }
        });
    }

    protected initVisibleColumns(): void {
        const components = this.columnComponents.toArray().filter(col => !(col instanceof SearchFieldComponent));
        this.columnComponentsArray = this.partnerListV2Service.getColumnComponentsArray(components, this.defaultProperties);
        this.displayedColumns = this.partnerListV2Service.getVisibleColumns(this.columnComponentsArray, this.defaultProperties, 'Partner', this.id);
        this.descriptions = this.partnerListV2Service.getColumnDescriptions(components);
        if (!this.sort) {
            this.sort = this.partnerListV2Service.setDefaultSort(this.displayedColumns);
        }
        this.editTableColumnsEnabled = !!this.id && this.columnComponentsArray.filter(c => c.displayMode != ColumnComponentDisplayMode.ALWAYS_HIDDEN).length > 1;
    }

    goToDetail(partner: Partner): void {
        let link;
        if (this.detailsPath) {
            link = ['/dashboard/partner_details', partner?.id, this.detailsPath];
        } else {
            link = ['/dashboard/partner_details', partner?.id];
        }
        this.navigationService.navigateTo(link);
    }

    addPartner(): void {
        this.navigationService.navigateTo(['/dashboard/partner_details/add'], { queryParams: { type: this.type } });
    }

    private getPartnerList(): void {
        this.partnerListV2Service.getPagedList(this.pageIndex, this.pageSize, this.sort, this.searchFields, this.advancedSearchBody, this.includeParentsOnly, this.includeAllPartners, this.type).then(pagedList => {
            this.updateElementList(pagedList);
            this.error = null;
        }).catch(err => this.error = ErrorUtility.getMessage(err));
    }

    refreshList(data?: { pageIndex: number, pageSize: number, advancedSearchBody: any, sort: string[] }): void {
        if (data) {
            this.pageIndex = data.pageIndex;
            this.pageSize = data.pageSize;
            this.advancedSearchBody = data.advancedSearchBody;
            this.sort = data.sort;
        }
        this.getPartnerList();
    }

    export(): void {
        const params = this.partnerListV2Service.getParams(this.searchFields, this.advancedSearchBody, this.includeParentsOnly, true, this.includeAllPartners, this.type);
        this.partnerListV2Service.downloadCSV(params, this.exportService.resolveExportFileNamePlaceholders(this.exportFileName));
    }

    protected subscribeToExportServices(): void {
        this.exportId = "partner-list-" + this.getNextId()
        this.exportService.subscribeToExport(this.exportId, this.title || "Partners").subscribe(() => this.export());
        this.subscribeToExportVisibility();
    }

    protected updateSnackbarButtons(): void {
        if (this.selectedElements.length) {
            const allElementsCount = this.allElementsSelected ? this.length : this.selectedElements.length;
            let dividerPosition = 0;
            this.snackbarButtons = [
                { type: ListSnackbarButtonType.SELECTED_ELEMENTS, label: allElementsCount + '', event: 'unselect' }
            ];
            if (!this.allElementsSelected) {
                this.snackbarButtons.push({ type: ListSnackbarButtonType.SIMPLE, label: 'selectAllButton', event: 'selectAll', icon: 'select_all' });
                dividerPosition++;
            }
            if (this.authenticationService.hasPermission(Permissions.DELETE_PARTNER)) {
                this.snackbarButtons.push({ type: ListSnackbarButtonType.SIMPLE, label: 'bulkDeleteProperty', event: 'bulkDelete', icon: 'delete' });
            }
            this.dividerIndexes = [dividerPosition];
        } else {
            this.snackbarButtons = null;
            this.dividerIndexes = null;
        }
    }

    doSnackbarAction(event: string): void {
        switch (event) {
            case 'unselect':
                this.clearSelectedElements();
                break;
            case 'selectAll':
                this.setAllElementsSelected();
                break;
            case 'bulkDelete':
                const params = this.partnerListV2Service.getParams(this.searchFields, this.advancedSearchBody, this.includeParentsOnly, false, this.includeAllPartners, this.type);
                this.openBulkDeleteDialog(params, BulkDeleteElementType.PARTNER);
                break;
        }
    }
}